import axios, { AxiosError } from 'axios';
import { API_GATEWAY_BASE_URL } from '@/core/global-data/constants.data';
import { clearAuthTokens } from '@/features/auth/guard/utils';
import router from '@/core/global-router';
import { toast } from 'sonner';

// ------------- AXIOS DEFAULTS SETTINGS -------------
// ---------------------------------------------------
const defaultURL = API_GATEWAY_BASE_URL;

axios.defaults.baseURL = defaultURL;

// ------------- GLOBAL ERROR HANDLER -------------

const handleUnauthorized = () => {
    // 1. Clear authentication tokens
    clearAuthTokens();

    // 2. Redirect to login page
    router.navigate('/login');

    // 3. Optional: Refresh the page to clear application state
    window.location.reload();
};

// ------------- CREATE AXIOS INTERCEPTORS -------------
// ----------------------------------------------------

axios.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
        const status = error.response?.status;
        const isUnauthorized = status === 401;

        if (isUnauthorized && !error.config?.url?.includes('/login')) {
            handleUnauthorized();
        }

        if (status === 403 && error.response?.data?.message) {
            toast.error(error.response?.data?.message);
        }

        return Promise.reject(error);
    },
);

// -------------------------------------------------------

export const endpoints = {
    auth: {
        login: '/login',
        profile: '/user',
        me: '/me',
        changePassword: '/change-password',
        forgotPassword: '/forgot-password',
        resetPassword: '/reset-password',
    },
    dashboard: {
        analytics: '/students-dashboard',
    },

    student: {
        create: '/students-store',
        update: '/students-update/',
        delete: '/students-delete/',
        list: '/students-paginated',
        select: '/students-simple',
        show: '/students-show/',
        block: '/students-add-to-black-list/',
        logs: '/logs/user',
        logscleanup: '/logs/cleanup/',


        dashboard: '/students-analytics',
        dashboardType: '/students-analytics-type',
        images: '/student-update-personal-images/',
        license: '/student-update-license-images/',
        dashboardNewStudents: '/student-new-dashboard',
        typeTrack: '/students-type-tracks/',
        typeTracks: '/students-type-tracks',
        exams: '/students/{student}/exams',
    },

    complaint: {
        list: '/student-complaints-paginated',
        show: '/student-complaints-show/',
        update: '/student-complaints-resolve/',
        // update: '/students-complaints/',
        create: '/student-complaints-store',
        delete: '/students-delete/',
        resolve: '/student-complaints-resolve/',
        dashboard: '/student-complaints-statistics',
        open: '/student-complaints-open',
    },

    instructors: {
        create: '/instructors-store',
        update: '/instructors-update/',
        delete: '/instructors-delete/',
        list: '/instructors-paginated',
        show: '/instructors-show/',
        select: '/instructors-simple',
        dashboard: '/instructors-analytics',
        CoursesList: '/instructors/{instructor}/courses',
        ProductsList: '/students/{student}/products',
    },

    cutomerContact: {
        list: '/student-emails-paginated',
        create: '/student-emails-store',
        dashboard: '/student-emails-statistics',
        show: '/student-emails-show/', //student-emails-show
        update: '/student-emails-update',
        email: '/students-emails/',
    },

    courses: {
        create: '/courses-store',
        update: '/courses-update',
        delete: '/courses-delete/',
        list: '/courses-paginated',
        show: '/courses-show',
        dashboard: '/courses-analytics',
        select: '/courses-simple',
        sessions: '/courses/{course}/sessions',
        sessionsRegenerate: '/courses/{course}/sessions-regenerate',
        sessionUpdateDelivered: '/course-sessions-update-delivered/',
        instructors: '/courses/{course}/instructors',
        exams: '/courses/{course}/exams',
    },

    courseExams: {
        list: '/course-exams-paginated',
        create: '/course-exams-store',
        update: '/course-exams-update/',
        delete: '/course-exams-delete/',
        show: '/course-exams-show/',
        storeResult: '/student-course-exams-store',
    },

    products: {
        list: '/student-products-paginated',
        create: '/student-products-store',
        update: '/student-products-update',
        delete: '/student-products-delete/',
        show: '/student-products-show',
        dashboard: '/student-products-analytics',
        select: '/student-products-simple',
    },

    courseEnrollments: {
        list: '/student-course-enrollments',
        enroll: '/student-course-enrollments-enroll',
        unenroll: '/student-course-enrollments-unenroll/',
        updateStatus: '/student-course-enrollments-update-status/',
        studentCourses: '/students/',
        courseStudents: '/courses/',
    },

    productEnrollments: {
        list: '/student-product-enrollments',
        enroll: '/student-product-enrollments-enroll',
        unenroll: '/student-product-enrollments-unenroll/',
        updateStatus: '/student-product-enrollments-update-status/',
        studentProducts: '/students/',
        productStudents: '/student-products/',
    },
    finance: {
        payments: '/orders-payment-analytics',
    },
    notification: {
        list: '/notification-list',
        count: '/notification-count',
        delete: '/notification-delete/',
        read: '/notification-mark-read/',
        allRead: '/notification-mark-all-read',
        mail: '/notification-mail',
        generate: '/notification-generate',
        sendEmail: '/notification-send-emails',
    },

    orderproducts: {
        list: '/product-orders-paginated',
        create: '/product-orders-store',
        update: '/product-orders-update',
        delete: '/product-orders-delete/',
        show: '/product-orders-show',
        dashboard: '/product-orders-analytics',
        post: '/website-product-service',
        get: '/website-products',
        // select: '/products-simple',
    },

    branch: {
        create: '/branches-store',
        list: '/branches-paginated',
        show: '/branches-show/',
        update: '/branches-update/',
        delete: '/branches-delete/',
        dashboard: 'branches-analytics',
        select: 'branches-simple',
    },

    employee: {
        list: '/users-paginated',
        show: '/users-show/',
        create: '/users-store',
        update: '/users-update/',
        delete: '/users-delete/',
        dashboard: '/users-analytics',
        select: '/users-simple',
        attendance: '/users-attendance',
        salary: '/users-salary/',
        salaryList: '/users-salary-list',
        logs: '/user-logs/user/',
        usersOrdersProducts: '/users-orders-and-product-orders/',
        enrollments: '/student-enrollments-by-user/{user}',
    },
    roles: {
        list: '/role-list',
        create: '/role-create',
        update: '/role-update/',
        delete: '/role-delete/',
        show: '/role-permissions/',
    },
    permissions: {
        list: '/permission-list',
        create: '/permission-create',
        update: '/permission-update/',
        delete: '/permission-delete/',
    },
    userLimits: {
        list: '/user-limits',
    },
    orders: {
        create: '/orders-store',
        update: '/orders-update',
        delete: '/orders-delete/',
        list: '/orders-paginated',
        show: '/orders-show',
        dashboard: '/orders-analytics',
        status: '/orders-update-status',
    },
    // subscriptions: {
    //     list: '/subscriptions-paginated',
    //     create: '/subscriptions-store',
    //     dashboard: '/subscriptions-analytics',
    //     update: '/subscriptions-update/',
    //     delete: '/subscriptions-delete/',
    //     show: '/subscriptions-show/',
    //     subscriptions: '/student-subscriptions',
    // },

    loan: {
        list: '/loan-request-list-paginate',
        show: '/loan-request-show/',
        update: '/loan-request-update/',
        create: '/loan-request-create',
        delete: '/loan-request-delete/',
    },
    letterRequests: {
        paginate: '/letter-request-list-paginate',
        create: '/letter-request-create',
        update: '/letter-request-update/',
        delete: '/letter-request-delete/',
    },
    extraServices: {
        list: '/service-request-list-paginate',
        show: '/service-request-show/',
        create: '/service-request-create',
        update: '/service-request-update/',
        delete: '/service-request-delete/',
    },
    employeeComplaint: {
        list: '/employee-complaint-list-paginate',
        show: '/employee-complaint-show/',
        update: '/employee-complaint-update/',
        create: '/employee-complaint-create',
        delete: '/employee-complaint-delete/',
    },
};
